import type { SchedulePreset } from '../../../types/frontend';

export const DEFAULT_SCHEDULE_HOUR = 9;
export const DEFAULT_SCHEDULE_MINUTE = 0;

const clampHour = (h: number): number => Math.max(0, Math.min(23, Math.floor(h)));
const clampMinute = (m: number): number => Math.max(0, Math.min(59, Math.floor(m)));

export const buildPresetCron = (
  preset: SchedulePreset,
  hour: number = DEFAULT_SCHEDULE_HOUR,
  minute: number = DEFAULT_SCHEDULE_MINUTE,
): string => {
  const h = clampHour(hour);
  const m = clampMinute(minute);
  switch (preset) {
    case 'hourly':
      return 'rate(1 hour)';
    case 'daily':
      return `cron(${m} ${h} * * ? *)`;
    case 'weekly':
      return `cron(${m} ${h} ? * MON *)`;
    case 'monthly':
      return `cron(${m} ${h} 1 * ? *)`;
    default:
      return 'rate(1 day)';
  }
};

export const getPresetFromExpression = (
  expression: string,
): SchedulePreset | undefined => {
  if (expression === 'rate(1 hour)') return 'hourly';
  // rate(1 day) intentionally not mapped to 'daily': it has no fixed time-of-day,
  // so loading it as a preset would silently rewrite to cron(0 9 * * ? *) on save.
  const cronMatch = expression.match(/^cron\((\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\)$/);
  if (!cronMatch) return undefined;
  const [, , , dom, , dow] = cronMatch;
  if (dow === 'MON') return 'weekly';
  if (dom === '1' && dow === '?') return 'monthly';
  if (dom === '*' && dow === '?') return 'daily';
  return undefined;
};

export const parseCronTime = (
  expression: string,
): { hour: number; minute: number } | null => {
  const cronMatch = expression.match(/^cron\((\d+)\s+(\d+)\s/);
  if (!cronMatch) return null;
  const minute = parseInt(cronMatch[1], 10);
  const hour = parseInt(cronMatch[2], 10);
  if (Number.isNaN(minute) || Number.isNaN(hour)) return null;
  return { hour: clampHour(hour), minute: clampMinute(minute) };
};
